import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, useToast, usePincodeLookup } from '@kss/ui';
import { apiClient, apiResources, type ApiResourceMeta } from '@kss/api';
import { ArrowLeft, Save } from 'lucide-react';
import {
  FieldInput, HIDDEN_ALWAYS, HIDDEN_ON_CREATE, coerceWithImages, humanise,
  orderFields, pickDescription, pickIcon, seedForm,
} from './GenericCrudForm';
import { provisionalCode } from '../../utils/nextCode';

/**
 * Per-resource auto-numbered fields (besides `code`, which CodeField
 * already handles). On a fresh create the field is pre-filled with a
 * generated value so the operator sees a number immediately.
 *   PosShift.shiftNumber — was blank (backend doesn't generate it).
 */
const RESOURCE_AUTONUMBER: Record<string, { field: string; prefix: string }> = {
  PosShift: { field: 'shiftNumber', prefix: 'SHIFT' },
  // Document-number fields the backend doesn't auto-generate (registry forms
  // #8/#15/#16/#17/#19/#20) — the form stamps a unique number on New.
  Shipment: { field: 'shipmentNumber', prefix: 'SHP' },
  PurchaseReturn: { field: 'returnNumber', prefix: 'PRTN' },
  PurchaseRequisition: { field: 'requisitionNumber', prefix: 'REQ' },
  GoodsReceiptNote: { field: 'grnNumber', prefix: 'GRN' },
  StockAdjustment: { field: 'adjustmentNumber', prefix: 'ADJ' },
  StockTransfer: { field: 'transferNumber', prefix: 'TRF' },
  StockTake: { field: 'stockTakeNumber', prefix: 'ST' },
};

/** Any field name that smells like an Indian PIN code. */
const PINCODE_FIELDS  = new Set(['pincode', 'postalCode', 'pinCode', 'postCode', 'zip', 'zipCode']);
const CITY_FIELDS     = new Set(['city', 'town', 'cityName']);
const STATE_FIELDS    = new Set(['state', 'province', 'stateName', 'region']);
const COUNTRY_FIELDS  = new Set(['country', 'countryName']);
/** FK columns to the Country master. Resolved via PIN → Country lookup. */
const COUNTRY_ID_FIELDS = new Set(['countryId', 'countryRefId']);

function findFirst(names: Set<string>, fieldList: Array<{ name: string }>): string | undefined {
  return fieldList.find((f) => names.has(f.name))?.name;
}

/**
 * Per-resource form-layout overrides, keyed by the registry resource
 * `name`. These let a specific entity tweak the otherwise-generic
 * layout without a hand-rolled page.
 *
 *   RESOURCE_HIDE_FIELDS  — field names hidden on this resource's form
 *                           (the column still exists server-side; we
 *                           just don't surface an input for it).
 *   RESOURCE_INLINE_ROWS  — groups of field names that must render
 *                           together on ONE row, pulled out of the
 *                           normal grid flow and placed at the end of
 *                           the form in their own n-column row.
 *
 * KOT (Kitchen Order Ticket): keep Table + Status side-by-side on a
 * single line. (Remarks is hidden GLOBALLY now — see HIDDEN_ALWAYS —
 * so it's no longer listed here.)
 */
const RESOURCE_HIDE_FIELDS: Record<string, Set<string>> = {
  // Reserved for per-resource field hiding. Empty today — `remarks` is
  // hidden everywhere via HIDDEN_ALWAYS.
};
const RESOURCE_INLINE_ROWS: Record<string, string[][]> = {
  Kot: [['tableId', 'status']],
};

/**
 * Look up Country.id by name (case-insensitive). Cached for the
 * lifetime of the page. Used when the entity exposes `countryId`
 * (FK) instead of plain `country` (string) — Company is the
 * canonical example. PIN lookup returns "India" → we resolve that
 * to `{id: 1}` and write the id.
 */
let countryCachePromise: Promise<Array<{ id: number; name: string; iso2?: string }>> | null = null;
async function resolveCountryId(name: string): Promise<number | null> {
  if (!name) return null;
  countryCachePromise ??= (async () => {
    try {
      const r = await apiClient.get('/master-data/Country');
      const raw = r.data;
      const list = Array.isArray(raw) ? raw
        : (raw && typeof raw === 'object' && Array.isArray((raw as { items?: unknown[] }).items)
            ? ((raw as { items: unknown[] }).items)
            : (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown[] }).data)
                ? ((raw as { data: unknown[] }).data) : []));
      return (list as Array<Record<string, unknown>>).map((c) => ({
        id: Number(c.id),
        name: String(c.name ?? ''),
        iso2: c.iso2 ? String(c.iso2) : undefined,
      }));
    } catch {
      return [];
    }
  })();
  const all = await countryCachePromise;
  const lname = name.toLowerCase();
  const hit = all.find(
    (c) => c.name.toLowerCase() === lname || (c.iso2 && c.iso2.toLowerCase() === lname),
  );
  return hit?.id ?? null;
}

/**
 * Full-page edit/create form for any registry resource.
 *
 *   /panel/api/:group/:resource/new   → create
 *   /panel/api/:group/:resource/:id   → edit
 *
 * Renders the exact same fields the old modal-based `GenericCrudForm`
 * showed (FieldInput is shared), but inside a real page shell:
 *   - sticky header with back arrow + entity icon + title +
 *     description
 *   - scrollable body with the field grid
 *   - sticky footer with Cancel / Save buttons
 *
 * Used as the default destination for the "New X" button across the
 * API browser, replacing the modal for every resource that doesn't
 * already have a dedicated hand-rolled page (TransactionEntryShell
 * presets, NewCustomer, NewSupplier, NewEmployee, …).
 */
export default function GenericFullPageForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const { group, resource, id: rawId } = useParams<{ group: string; resource: string; id?: string }>();
  const isEdit = !!rawId && rawId !== 'new';
  const id = isEdit ? rawId! : null;

  const meta = useMemo<ApiResourceMeta | undefined>(
    () => apiResources.find((r) => r.group === group && r.name === resource),
    [group, resource],
  );

  const visibleFields = useMemo(
    () => {
      if (!meta) return [];
      // Build the set of FK field stems present on this entity so we can
      // hide their legacy string twins. E.g. Employee has BOTH
      // `department: string` AND `departmentId: number` — keep only the
      // FK form so the operator doesn't get two "Department" inputs.
      const fkStems = new Set<string>();
      for (const f of meta.fields) {
        if (f.kind === 'number' && f.name.endsWith('Id') && !f.isId) {
          fkStems.add(f.name.slice(0, -2)); // "departmentId" → "department"
        }
      }
      const resourceHidden = RESOURCE_HIDE_FIELDS[meta.name];
      return orderFields(meta.fields.filter((f) => {
        if (HIDDEN_ALWAYS.has(f.name)) return false;
        if (resourceHidden?.has(f.name)) return false;
        if (f.isId && f.name !== 'code') return false;
        if (HIDDEN_ON_CREATE.has(f.name)) return isEdit;
        // Legacy string field whose FK twin exists — hide it.
        if (f.kind === 'string' && fkStems.has(f.name)) return false;
        return true;
      }));
    },
    [meta, isEdit],
  );

  const [form, setForm] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Auto-derive the row's `name` column from a picked FK. The audit
  // `name` field on entities like Attendance / LeaveApplication /
  // PayrollRun / PerformanceReview is meant to be human-readable
  // ("Sample Attendance"). When the operator picks an Employee FK, the
  // form should stamp "<employee name> - <date>" or similar into name
  // automatically so they don't have to type it twice.
  //
  // Triggered whenever the form's primary FK (e.g. employeeId) changes
  // and `name` is currently empty/audit-stub.
  useEffect(() => {
    // Prefer the referenced-invoice FK so that on a Payment / Return form,
    // picking the Purchase/Sales Invoice stamps its number into `name` (#13/#14).
    // Otherwise fall back to the party/product FK (e.g. Attendance → employee).
    const primaryFk =
      visibleFields.find((f) => !f.isId && /^(purchaseInvoiceId|salesInvoiceId)$/i.test(f.name)) ??
      visibleFields.find(
        (f) => f.kind === 'number' && f.name.endsWith('Id') && !f.isId &&
          /(employee|customer|supplier|product)/i.test(f.name),
      );
    if (!primaryFk) return;
    const fkValue = form[primaryFk.name];
    if (fkValue == null || fkValue === '') return;
    const currentName = String(form.name ?? '').trim();
    // Only overwrite if name is empty or matches the auto-seeded stub.
    if (currentName && !/^Sample\s/i.test(currentName) && currentName !== '0') return;
    void (async () => {
      try {
        const target = (await import('./fkResolver')).getFkOptions(primaryFk.name);
        const opts = await target;
        const match = opts.find((o) => String(o.id) === String(fkValue));
        if (match) {
          setForm((f) => ({ ...f, name: match.label }));
        }
      } catch { /* tolerate — auto-derive is best-effort */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFields, JSON.stringify(visibleFields.map((f) => form[f.name]))]);

  // Auto-fill City / State / Country from PIN code if the entity has
  // those fields. Works for Company (pincode/city/state/countryId),
  // Address, and any other registry resource with the address quartet.
  const pinFieldName       = findFirst(PINCODE_FIELDS,    visibleFields);
  const cityFieldName      = findFirst(CITY_FIELDS,       visibleFields);
  const stateFieldName     = findFirst(STATE_FIELDS,      visibleFields);
  const countryFieldName   = findFirst(COUNTRY_FIELDS,    visibleFields);
  const countryIdFieldName = findFirst(COUNTRY_ID_FIELDS, visibleFields);
  const pinValue = pinFieldName ? String(form[pinFieldName] ?? '') : '';
  const pinLookup = usePincodeLookup(pinValue);
  useEffect(() => {
    if (pinLookup.status !== 'ok') return;
    setForm((f) => {
      const next = { ...f };
      if (cityFieldName    && !next[cityFieldName])    next[cityFieldName]    = pinLookup.city    ?? next[cityFieldName];
      if (stateFieldName   && !next[stateFieldName])   next[stateFieldName]   = pinLookup.state   ?? next[stateFieldName];
      if (countryFieldName && !next[countryFieldName]) next[countryFieldName] = pinLookup.country ?? next[countryFieldName];
      return next;
    });
    // For entities like Company that use `countryId` (FK) instead of
    // plain `country` (string), resolve the country name to its id and
    // write that. Runs async after the state update above.
    if (countryIdFieldName && pinLookup.country) {
      void (async () => {
        const cid = await resolveCountryId(pinLookup.country!);
        if (cid != null) {
          setForm((f) => (f[countryIdFieldName] ? f : { ...f, [countryIdFieldName]: cid }));
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinLookup.status, pinLookup.city, pinLookup.state, pinLookup.country]);

  // Auto-fill amount / sub-total / totals from a referenced invoice. When a
  // PurchaseInvoice / SalesInvoice FK is picked on a Payment / Return form,
  // pull that invoice's figures so the operator doesn't re-key them
  // (Payment → amount = balance/grandTotal; Return → subTotal/tax/grandTotal).
  // Tracks the last invoice we prefilled from, so a re-select re-fills (#15).
  const prefilledInvRef = useRef<string | null>(null);
  useEffect(() => {
    const invFk = visibleFields.find(
      (f) => !f.isId && /^(purchaseInvoiceId|salesInvoiceId)$/i.test(f.name),
    );
    if (!invFk) return;
    const invId = form[invFk.name];
    if (invId == null || invId === '' || String(invId) === '0') return;
    // Did the SELECTED INVOICE change since we last prefilled? If so, the
    // invoice-derived money fields must FOLLOW the new invoice — the old
    // "only fill when empty" guard locked the amount to the first-picked
    // invoice and never updated on re-select (#15).
    const invChanged = String(invId) !== prefilledInvRef.current;
    let cancelled = false;
    void (async () => {
      try {
        const target = (await import('./fkResolver')).lookupFkTarget(invFk.name);
        if (!target || cancelled) return;
        const res = await apiClient.get(target.basePath);
        const d = res.data;
        const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
        const inv = rows.find((r) => String(r.id) === String(invId));
        if (!inv || cancelled) return;
        const has = (n: string) => visibleFields.some((f) => f.name === n);
        const num = (v: unknown) => Number(v) || 0;
        setForm((f) => {
          const next = { ...f };
          const empty = (n: string) => next[n] == null || next[n] === '' || Number(next[n]) === 0;
          // Money fields: overwrite when the invoice changed (or still empty).
          const fill = (n: string) => invChanged || empty(n);
          if (has('amount') && fill('amount')) next.amount = num(inv.balance ?? inv.grandTotal ?? inv.totalAmount);
          if (has('subTotal') && fill('subTotal')) next.subTotal = num(inv.subTotal);
          if (has('taxAmount') && fill('taxAmount')) next.taxAmount = num(inv.taxAmount);
          if (has('grandTotal') && fill('grandTotal')) next.grandTotal = num(inv.grandTotal ?? inv.totalAmount);
          // Party: only when empty (don't churn a manually chosen party).
          if (has('supplierId') && empty('supplierId') && inv.supplierId != null) next.supplierId = inv.supplierId;
          if (has('customerId') && empty('customerId') && inv.customerId != null) next.customerId = inv.customerId;
          return next;
        });
        prefilledInvRef.current = String(invId);
      } catch { /* best-effort prefill */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFields, JSON.stringify(visibleFields.map((f) => form[f.name]))]);

  // Load the row on edit. Uses ?id=... so it works against the live
  // list endpoint without needing a per-resource by-id route.
  useEffect(() => {
    if (!meta) return;
    if (!isEdit || !id) {
      const seeded = seedForm(visibleFields, null);
      // Auto-fill a per-resource number field (e.g. PosShift.shiftNumber)
      // so it's populated/visible on create instead of blank.
      const an = RESOURCE_AUTONUMBER[meta.name];
      if (an && visibleFields.some((f) => f.name === an.field) && !seeded[an.field]) {
        seeded[an.field] = provisionalCode(an.prefix);
      }
      setForm(seeded);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient.get(meta.basePath).then((res) => {
      if (cancelled) return;
      const rows = (res.data as Array<Record<string, unknown>>) ?? [];
      const found = rows.find((r) => String(r[meta.idField] ?? r.id ?? '') === String(id));
      setForm(seedForm(visibleFields, found ?? null));
    }).catch((e) => {
      if (cancelled) return;
      setErr(e instanceof Error ? e.message : 'Load failed');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [meta, isEdit, id, visibleFields]);

  if (!meta) {
    return (
      <div className="-m-6 p-4">
        <Alert variant="warning">
          Resource not in registry: {group}/{resource}
        </Alert>
      </div>
    );
  }

  function set(name: string, value: unknown) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function save() {
    setSaving(true); setErr(null);
    try {
      const payload = await coerceWithImages(form, visibleFields, isEdit);
      if (isEdit) {
        await apiClient.put(meta!.basePath, payload);
        toast.success('Saved', `${meta!.name} updated`);
      } else {
        await apiClient.post(meta!.basePath, payload);
        toast.success('Saved', `${meta!.name} created`);
      }
      navigate(`/panel/api/${group}/${resource}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(false);
    }
  }

  const Icon = pickIcon(meta.name);

  // Split visibleFields into three render bands:
  //   1. main grid  — the bulk of the fields
  //   2. inline rows — per-resource field pairs kept on one line
  //                    (e.g. KOT's Table + Status)
  //   3. trailing    — free-text Description / Notes, rendered LAST so
  //                    the inline row (Table + Status) appears BEFORE
  //                    Description (per request). Only split out when the
  //                    resource actually has an inline row — otherwise
  //                    Description keeps its normal position.
  const inlineRowNames = RESOURCE_INLINE_ROWS[meta.name] ?? [];
  const inlineFieldSet = new Set(inlineRowNames.flat());
  const inlineRows = inlineRowNames
    .map((names) =>
      names
        .map((n) => visibleFields.find((f) => f.name === n))
        .filter((f): f is NonNullable<typeof f> => !!f),
    )
    .filter((row) => row.length > 0);
  const hasInline = inlineRows.length > 0;
  const TRAILING_FIELDS = new Set(['description', 'notes']);
  const mainFields = visibleFields.filter(
    (f) => !inlineFieldSet.has(f.name) && !(hasInline && TRAILING_FIELDS.has(f.name)),
  );
  const trailingFields = hasInline
    ? visibleFields.filter((f) => TRAILING_FIELDS.has(f.name))
    : [];

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col bg-surface-app">
      {/* Sticky header */}
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(`/panel/api/${group}/${resource}`)}
          className="grid h-9 w-9 place-items-center rounded-md border border-border hover:bg-surface-card-hover"
          aria-label="Back to list"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold text-content-primary">
            {isEdit ? 'Edit' : 'New'} {humanise(meta.name)}
          </h1>
          <p className="truncate text-[11px] text-content-muted">
            {pickDescription(meta.name, meta.group)}
          </p>
        </div>
      </header>

      {/* Scrollable body */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

        {loading ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">Loading…</div>
        ) : (
          <Card className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {mainFields.map((f) => (
                <FieldInput
                  key={f.name}
                  field={f}
                  value={form[f.name]}
                  onChange={(v) => set(f.name, v)}
                  selfResource={meta}
                />
              ))}
            </div>

            {/* Per-resource inline rows — fields the resource wants kept
                together on a single line (e.g. KOT's Table + Status).
                Rendered BEFORE the trailing Description band. */}
            {inlineRows.map((rowFields, i) => (
              <div
                key={`inline-${i}`}
                className="mt-3 grid gap-3 sm:grid-cols-2"
                style={{ gridTemplateColumns: `repeat(${rowFields.length}, minmax(0, 1fr))` }}
              >
                {rowFields.map((f) => (
                  <FieldInput
                    key={f.name}
                    field={f}
                    value={form[f.name]}
                    onChange={(v) => set(f.name, v)}
                    selfResource={meta}
                  />
                ))}
              </div>
            ))}

            {/* Trailing free-text band (Description / Notes) — kept LAST
                so the inline Table + Status row sits above it. */}
            {trailingFields.length > 0 && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {trailingFields.map((f) => (
                  <FieldInput
                    key={f.name}
                    field={f}
                    value={form[f.name]}
                    onChange={(v) => set(f.name, v)}
                    selfResource={meta}
                  />
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Sticky footer */}
      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-surface-card px-4 py-3">
        <div className="text-xs text-content-muted">
          {isEdit ? `Editing ${meta.name} #${id}` : `Adding a new ${humanise(meta.name).toLowerCase()}`}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/panel/api/${group}/${resource}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading}>
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </footer>
    </div>
  );
}

